"use client";

import { useEffect, useState } from "react";
import type { NodeViewProps } from "@tiptap/react";

/**
 * Whether an atom node view is part of the current editor selection.
 *
 * TipTap's `selected` prop only reflects a ProseMirror NodeSelection (clicking
 * the node). A text or select-all range that spans the node leaves it `false`
 * (since `@tiptap/react` 3.31), so we also flag the node while a non-empty
 * selection fully covers it. Read-only editors only report node selections.
 */
export function useNodeViewSelected({
  node,
  editor,
  getPos,
  selected,
}: Pick<NodeViewProps, "node" | "editor" | "getPos" | "selected">): boolean {
  const editable = editor.isEditable;
  const [rangeSelected, setRangeSelected] = useState(false);

  useEffect(() => {
    if (!editable || typeof editor.on !== "function") {
      setRangeSelected(false);
      return;
    }
    const sync = () => {
      const pos = typeof getPos === "function" ? getPos() : null;
      const selection = editor.state?.selection;
      if (pos == null || Number.isNaN(pos) || !selection) {
        setRangeSelected(false);
        return;
      }
      const { from, to, empty } = selection;
      const size = node.nodeSize ?? 1;
      setRangeSelected(!empty && from <= pos && to >= pos + size);
    };
    sync();
    editor.on("selectionUpdate", sync);
    editor.on("transaction", sync);
    return () => {
      editor.off("selectionUpdate", sync);
      editor.off("transaction", sync);
    };
  }, [editor, getPos, node, editable]);

  return Boolean(selected) || rangeSelected;
}
